import { useState } from "react";
import { NavLink } from "react-router-dom";
import { BarChart3, Bell, Blocks, Building2, LayoutDashboard, LifeBuoy, Lightbulb, LogOut, MapPin, Megaphone, Package, Plug, ScrollText, ShieldCheck, UsersRound, Webhook, X } from "lucide-react";
import { useAuth } from "../../auth/context";
import { cx, initials } from "../../utils";
import AccountDialog from "../AccountDialog";

const ROLE_LABEL = { admin: "Admin", member: "Member", viewer: "Viewer" };

const navGroups = [
  {
    label: "Overview",
    items: [
      { to: "/", end: true, label: "Dashboard", icon: LayoutDashboard },
      { to: "/analytics", end: false, label: "Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Apps",
    items: [
      { to: "/apps", end: false, label: "Apps", icon: Package },
      { to: "/installs", end: false, label: "Installs", icon: Plug },
    ],
  },
  {
    label: "Monitoring",
    items: [
      { to: "/activity", end: false, label: "Activity logs", icon: ScrollText },
      { to: "/app-events", end: false, label: "App events", icon: Webhook },
    ],
  },
  {
    label: "Customers",
    items: [
      { to: "/companies", end: false, label: "Companies", icon: Building2 },
      { to: "/locations", end: false, label: "Locations", icon: MapPin },
      { to: "/users", end: false, label: "Users", icon: UsersRound },
    ],
  },
  {
    label: "Engage",
    items: [
      { to: "/support", end: false, label: "Support", icon: LifeBuoy },
      { to: "/feature-requests", end: false, label: "Feature requests", icon: Lightbulb },
      { to: "/announcements", end: false, label: "Announcements", icon: Megaphone },
      { to: "/notifications", end: false, label: "Notifications", icon: Bell },
    ],
  },
  {
    label: "Workspace",
    adminOnly: true,
    items: [{ to: "/team", end: false, label: "Team", icon: ShieldCheck }],
  },
];

const linkClass = ({ isActive }) =>
  cx(
    "group relative flex items-center gap-2.5 rounded-lg px-3 h-9 text-[13px] font-medium transition-colors",
    isActive ? "bg-white/10 text-white" : "text-ink-400 hover:bg-white/5 hover:text-ink-100",
  );

const Sidebar = ({ onNavigate }) => {
  const { user, logout, isAdmin, role } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const groups = navGroups.filter((g) => !g.adminOnly || isAdmin);

  return (
    <div className="flex h-full w-64 flex-col bg-ink-950 text-ink-300">
      {/* Brand */}
      <div className="flex h-14 items-center gap-2.5 px-5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-gradient shadow-brand-glow">
          <Blocks size={16} className="text-white" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold tracking-tight text-white">
            Marketplace
          </p>
          <p className="text-[11px] leading-tight text-ink-500">Super admin</p>
        </div>
        {onNavigate && (
          <button
            onClick={onNavigate}
            className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-white/5 hover:text-white lg:hidden"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {groups.map((group, g) => (
          <div key={group.label} className={cx("space-y-0.5", g > 0 && "pt-4")}>
            <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-600">
              {group.label}
            </p>
            {group.items.map(({ to, end, label, icon: Icon }) => (
              <NavLink key={to} to={to} end={end} className={linkClass} onClick={onNavigate}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-brand-400" />
                    )}
                    <Icon size={15} strokeWidth={2.25} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Account */}
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-1 rounded-lg">
          <button
            type="button"
            onClick={() => setAccountOpen(true)}
            title="Your account"
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-white/5"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-gradient text-[11px] font-bold text-white">
              {initials(user?.username || user?.email || "")}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-[13px] font-medium text-ink-100">{user?.username || "Admin"}</span>
                {role && <span className="shrink-0 rounded bg-white/10 px-1.5 py-px text-[10px] font-medium text-ink-300">{ROLE_LABEL[role]}</span>}
              </span>
              <span className="block truncate text-[11px] text-ink-500">{user?.email}</span>
            </span>
          </button>
          <button
            onClick={logout}
            title="Sign out"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-white/5 hover:text-rose-400"
            aria-label="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
      {accountOpen && <AccountDialog onClose={() => setAccountOpen(false)} />}
    </div>
  );
};

export default Sidebar;
